/**
 * Uses rest parameters syntax to take any number of arguments and convert them into an array called classNames.
 * Filters out any falsy values (e.g., null, undefined, false, 0, '') from the array.
 * Joins the remaining class names into a single string separated by spaces.
 */
export const joinClasses = (...classNames) => classNames.filter(Boolean).join(' ');

/**
 * Takes the base class names object and a custom class names object, and merges them together.
 * Creates a Set of all keys allowing duplicates to be removed by making custom keys overwrite base keys.
 * Creates a new object by mapping over the unique keys and calling their functions with the current state to get their class names.
 * Uses joinClasses to combine the base and custom class names for each key.
 * Returns the new object with the merged class name functions.
 */
export const mergeClassNames = (base, custom = {}) => {
  const keys = new Set([...Object.keys(base), ...Object.keys(custom)]); // Ensures all keys are included and custom keys overwrite base keys

  return Object.fromEntries(
    [...keys].map((key) => [key, (state) => joinClasses(base[key]?.(state), custom[key]?.(state))]),
  );
};

/**
 * Validates the given style object.
 * Returns undefined if the style is not a valid object or is an array.
 * Otherwise, returns the style object itself.
 */
export const getValidStyle = (style) => {
  if (!style || typeof style !== 'object' || Array.isArray(style)) return undefined;
  return style;
};
